/**
 * Get device session ID from Checkout.com Risk.js
 * Based on withCkoGetDeviceSessionIdApi from common/config/utils.js
 */

import { asyncLoadScript } from './asyncLoadScript';
import type { RiskModelConfig } from '../types';

/**
 * Load Risk.js script and get device session ID (riskjs)
 */
const getDeviceSessionIdRiskJs = async ({ publicKey, riskJsUrl }: { publicKey: string; riskJsUrl: string }): Promise<string> => {
  await asyncLoadScript(riskJsUrl, window.Risk);

  if (!window.Risk?.init) {
    throw new Error('Risk.js not available');
  }

  const risk = window.Risk.init(publicKey);
  return risk.publishRiskData();
};

/**
 * Load Risk.js script and get device session ID (riskjs20)
 */
const getDeviceSessionIdRiskJs20 = async ({ publicKey, riskJsUrl, jsIntegrity }: { publicKey: string; riskJsUrl: string; jsIntegrity?: string }): Promise<string> => {
  await asyncLoadScript(riskJsUrl, window.Risk, jsIntegrity);

  if (!window.Risk?.create) {
    throw new Error('Risk.js 2.0 not available');
  }

  const risk = await window.Risk.create(publicKey);
  return risk.publishRiskData();
};

/**
 * Get device session ID from Checkout.com Risk.js
 *
 * This is used for fraud detection. The device session ID is generated by loading
 * the Risk.js script and calling publishRiskData().
 *
 * @param riskModel - Risk model version: 'riskjs' (v1) or 'riskjs20' (v2)
 * @param riskModelConfig - Configuration with script URL, public key, etc.
 * @returns Device session ID string, or null if unavailable/unsupported
 */
export const getDeviceSessionId = async (
  riskModel: string | null | undefined,
  riskModelConfig: RiskModelConfig | null | undefined
): Promise<string | null> => {
  // Validate inputs
  if (!riskModel || !riskModelConfig?.publicKey) {
    return null;
  }

  try {
    // Handle different Risk.js versions
    if (riskModel === 'riskjs') {
      return await getDeviceSessionIdRiskJs({
        publicKey: riskModelConfig.publicKey,
        riskJsUrl: riskModelConfig.jsSrc
      });
    }

    if (riskModel === 'riskjs20') {
      return await getDeviceSessionIdRiskJs20({
        publicKey: riskModelConfig.publicKey,
        riskJsUrl: riskModelConfig.jsSrc,
        jsIntegrity: riskModelConfig.jsIntegrity
      });
    }
  } catch (error) {
    // Don't fail the payment if device session ID generation fails
    // eslint-disable-next-line no-console
    console.error('Failed to get device session ID:', error);
    return null;
  }

  // Unsupported risk model version
  return null;
};
